import Config from '../../commons/config/Config.js';

/**
 * ericCorpusService — fábrica de servicio para el API de documentos del corpus
 * de ERIC Assistant (procedimientos internos que sube la empresa).
 *
 * Recibe un AjaxHandler ya suscripto (el del componente) para que el lifecycle
 * subscribe/unsubscribe sea responsabilidad del componente, no del servicio.
 * Sigue el mismo patrón que ericConversacionesService.js para listar().
 *
 * subir() es la excepción deliberada: NO usa ajaxHandler.fetchMultipart. Ese
 * método trata cualquier status >= 404 (incluido nuestro 422 de rechazo — PDF
 * escaneado, formato no soportado, presupuesto excedido) como error fatal:
 * dispara un popup genérico ("Error interno del sistema") y rechaza la promise
 * con un TypeError que descarta el body de la respuesta antes de que el caller
 * pueda leer `detalle`. Como acá el 422 es una respuesta esperada del negocio
 * con un motivo específico para mostrar, subir() usa `fetch` nativo del
 * browser para tener control total sobre la respuesta de error.
 *
 * Uso:
 *   var svc = crearEricCorpusService(this.ajaxHandler);
 *   svc.listar().then(function(res) { ... });
 *   svc.subir(archivo).then(function(doc) { ... }).catch(function(err) { ... });
 *
 * docker exec -it vecfleet-web-workspace-1 bash -c "cd /var/www && CI=true npm test -- --testPathPattern='ericCorpusService'"
 */

/**
 * Lista los documentos del corpus y el presupuesto de contexto en uso.
 *
 * @param {object} ajaxHandler - instancia de AjaxHandler ya suscripta en el componente
 * @returns {{ listar: Function, subir: Function }}
 */
function crearEricCorpusService(ajaxHandler) {
  /**
   * @returns {Promise<{ documentos: Array, presupuestoTotal: number, presupuestoEnUso: number }>}
   */
  function listar() {
    return ajaxHandler
      .fetch('/eric/corpus/documentos', {
        method: 'GET',
        silent: true,
      })
      .then(function (response) {
        if (!response || response.status !== 200) {
          return { documentos: [], presupuestoTotal: 60000, presupuestoEnUso: 0 };
        }
        return response.json().then(function (data) {
          var payload = data && data.data ? data.data : {};
          return {
            documentos: payload.documentos ? payload.documentos : [],
            presupuestoTotal: payload.presupuesto_total != null ? payload.presupuesto_total : 60000,
            presupuestoEnUso: payload.presupuesto_en_uso != null ? payload.presupuesto_en_uso : 0,
          };
        });
      })
      .catch(function () {
        return { documentos: [], presupuestoTotal: 60000, presupuestoEnUso: 0 };
      });
  }

  /**
   * Texto que se le muestra al usuario cuando la subida se rechaza.
   *
   * `detalle` llega en dos formas según quién frene el archivo: string en los
   * 422 del micro (ERIC lo leyó y no le sirvió), y lista de strings en los 400
   * de la validación del monolito, porque ApiError siempre arma `detalle` como
   * array. Sin aplanar la lista, React recibe un array y el aviso sale con los
   * mensajes pegados sin separación.
   *
   * @param {{detalle?: string|string[]}|null} data cuerpo JSON de la respuesta
   * @returns {string}
   */
  function motivoDeRechazo(data) {
    var detalle = data && data.detalle;

    if (Array.isArray(detalle)) {
      detalle = detalle.join(' ');
    }

    return detalle || 'No pudimos cargar el documento.';
  }

  /**
   * Sube un documento nuevo al corpus.
   *
   * El archivo viaja como multipart (campo `archivo`); el monolito lo reenvía
   * al micro en base64 dentro del JSON.
   *
   * @param {File} archivo
   * @returns {Promise<{ id: number, titulo: string, tokensEstimados: number, estado: string }>}
   *          rechaza con Error cuyo .mensaje trae el motivo legible del rechazo
   *          (PDF escaneado, formato no soportado, presupuesto excedido, etc.)
   */
  function subir(archivo) {
    var formData = new FormData();
    formData.append('archivo', archivo);

    // fetch nativo, no ajaxHandler.fetchMultipart: ver el porqué en el docblock
    // del archivo. Sin 'Content-Type' explícito — el browser lo arma solo con
    // el boundary correcto del multipart al ver que el body es un FormData.
    return fetch(Config.get('apiUrlBase') + '/eric/corpus/documentos', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Authorization-Token': localStorage.getItem('token'),
      },
      body: formData,
    })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return null; // body no-JSON (ej. 413 del proxy): motivo cae al fallback abajo
          })
          .then(function (data) {
            if (response.status !== 201) {
              var motivo = motivoDeRechazo(data);
              var error = new Error(motivo);
              error.mensaje = motivo;
              throw error;
            }
            var payload = data && data.data ? data.data : {};
            return {
              id: payload.id,
              titulo: payload.titulo || archivo.name,
              tokensEstimados: payload.tokens_estimados || 0,
              estado: payload.estado || 'activo',
              // El backend usa `detalle` en un 201 para avisos que NO son error:
              // hoy, que el archivo ya se había subido antes. Sin esto el aviso
              // se pierde y el usuario duplica documentos sin enterarse.
              aviso: (data && data.detalle) || null,
            };
          });
      });
  }

  /**
   * Pausa ('inactivo') o reactiva ('activo') un documento.
   *
   * Acá sí va por ajaxHandler.fetch: la respuesta buena es 200 y los errores
   * posibles (403, 404, 500) son fatales de verdad, no rechazos de negocio con
   * motivo para mostrar — el comportamiento genérico del handler es el correcto.
   *
   * @param {number} id
   * @param {'activo'|'inactivo'} estado
   * @returns {Promise<{ id: number, estado: string, presupuestoEnUso: number }>}
   */
  function cambiarEstado(id, estado) {
    return ajaxHandler
      .fetch('/eric/corpus/documentos/' + id, {
        method: 'PATCH',
        body: JSON.stringify({ estado: estado }),
        silent: true,
      })
      .then(function (response) {
        if (!response || response.status !== 200) {
          throw new Error('No se pudo cambiar el estado del documento.');
        }
        return response.json().then(function (data) {
          var payload = data && data.data ? data.data : {};
          return {
            id: payload.id,
            estado: payload.estado,
            presupuestoEnUso: payload.presupuesto_en_uso != null ? payload.presupuesto_en_uso : 0,
          };
        });
      });
  }

  return { listar: listar, subir: subir, cambiarEstado: cambiarEstado };
}

export default crearEricCorpusService;
